import { Link, useLocation } from "@tanstack/react-router";
import { useState } from "react";
import { Building2, ShieldCheck } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "~/components/ui/sidebar";
import { useAuthenticatedUser } from "~/hooks/useAuthenticatedUser";
import { LogoutModal } from "../../auth/logout-modal";
import { cn } from "~/utils/cn";
import { useMyOrganization } from "~/hooks/useOrganization";
import { NAVIGATION_ITEMS, ORG_ITEM, SETTINGS_ITEMS, UNIVERSITY_STATS_ITEM, UNIVERSITY_ACCESS_ITEM } from "~/constants/sidebar";
import { ToggleSidebarButton } from "./toggle-sidebar-button";
import { useTenantPath } from "~/hooks/use-tenant-path";
import { getTenantSlugFromPath } from "~/utils/tenant";
import ExternalLink from "./external-link";

const isRouteActive = (itemHref: string, currentPath: string): boolean => {
  // Strip tenant prefix for matching
  const tenantSlug = getTenantSlugFromPath();
  const effectivePath = tenantSlug && currentPath.startsWith(`/${tenantSlug}`)
    ? currentPath.slice(`/${tenantSlug}`.length) || "/"
    : currentPath;
  return effectivePath.startsWith(itemHref);
};

export function AppSidebar() {
  const location = useLocation();
  const { user, capabilities, isPublisherAccount, isUniversityAccount } = useAuthenticatedUser();
  const { organization } = useMyOrganization();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const isCollapsed = state === "collapsed";
  const tenantPath = useTenantPath();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const publicFacingDomain = import.meta.env.VITE_PUBLIC_URL || "https://qa.proudlyhuman.org";

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const canManageOrganization = !!capabilities?.canManageOrganization;
  const hasOrganization = !!organization && isPublisherAccount;

  return (
    <Sidebar collapsible="icon" className="border-r">
      <ToggleSidebarButton />

      <SidebarHeader className={cn(organization?.branding?.logo_url ? "p-4" : "p-4 md:p-0")}>
        {organization?.branding?.logo_url && (
          <div
            className={cn(
              "flex items-center gap-2 mb-3",
              isCollapsed ? "justify-center" : "justify-start"
            )}
          >
            <img
              src={organization.branding.logo_url}
              alt={organization.displayName}
              className={cn(
                "object-contain shrink-0",
                isCollapsed ? "h-8 w-8" : "max-h-12 w-auto"
              )}
            />
            {/* {!isCollapsed && organization.displayName && (
              <span className="text-sm font-semibold">
                {organization.displayName}
              </span>
            )} */}
          </div>
        )}
        <div
          className={cn(
            "flex items-center gap-3 md:hidden",
            isCollapsed && "justify-center"
          )}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-medium">
            {user?.firstName?.charAt(0).toUpperCase()}
          </div>
          {!isCollapsed && (
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">
                {user?.firstName} {user?.lastName}
              </span>
            </div>
          )}
        </div>
      </SidebarHeader>

      {organization?.branding?.logo_url && <SidebarSeparator />}

      <SidebarContent>
        {/* Navigation Group */}
        <SidebarGroup>
          <SidebarGroupLabel>Dashboard</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {NAVIGATION_ITEMS.map((item) => {
                const isActive = !item.external && isRouteActive(item.href, location.pathname);
                const Icon = item.icon;
                const name = hasOrganization && item.href === "/dashboard" ? "Applications" : item.name;

                return (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive}
                      tooltip={name}
                    >
                      {item.external ? (
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={handleNavClick}
                        >
                          <Icon className="h-4 w-4" />
                          <span>{name}</span>
                        </a>
                      ) : (
                        <Link
                          to={tenantPath(item.href)}
                          onClick={handleNavClick}
                        >
                          <Icon className="h-4 w-4" />
                          <span>{name}</span>
                        </Link>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}

              {capabilities?.canViewUniversityStats && isUniversityAccount && (() => {
                const StatsIcon = UNIVERSITY_STATS_ITEM.icon;
                return (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isRouteActive(UNIVERSITY_STATS_ITEM.href, location.pathname)}
                      tooltip={UNIVERSITY_STATS_ITEM.name}
                    >
                      <Link to={tenantPath(UNIVERSITY_STATS_ITEM.href)} onClick={handleNavClick}>
                        <StatsIcon className="h-4 w-4" />
                        <span>{UNIVERSITY_STATS_ITEM.name}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })()}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Settings</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {SETTINGS_ITEMS.map((item) => {
                const Icon = item.icon;
                return (
                  <SidebarMenuItem key={item.name}>
                    <SidebarMenuButton
                      asChild
                      isActive={isRouteActive(item.href, location.pathname)}
                      tooltip={item.name}
                    >
                      <Link
                        to={tenantPath(item.href)}
                        onClick={handleNavClick}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{item.name}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}

              {capabilities?.canManageUniversityAccess && isUniversityAccount && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={isRouteActive(UNIVERSITY_ACCESS_ITEM.href, location.pathname)}
                    tooltip={UNIVERSITY_ACCESS_ITEM.name}
                  >
                    <Link to={tenantPath(UNIVERSITY_ACCESS_ITEM.href)} onClick={handleNavClick}>
                      <ShieldCheck className="h-4 w-4" />
                      <span>{UNIVERSITY_ACCESS_ITEM.name}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              {hasOrganization && canManageOrganization && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={isRouteActive(ORG_ITEM.href, location.pathname)}
                    tooltip={organization?.displayName || ORG_ITEM.name}
                  >
                    <Link to={tenantPath(ORG_ITEM.href)} onClick={handleNavClick}>
                      <Building2 className="h-4 w-4" />
                      <span>Organization Account</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}

              <SidebarMenuItem>
                <LogoutModal
                  open={showLogoutModal}
                  onOpenChange={setShowLogoutModal}
                />
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {!isCollapsed && (
          <SidebarFooter className="h-full flex flex-col justify-end p-4">
            <div className="bg-background border rounded-md p-3">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
                Resources
              </p>
              <ul className="flex flex-col gap-3">
                <li>
                  <ExternalLink title="About" href={`${publicFacingDomain}#about`} />
                </li>
                <li>
                  <ExternalLink title="FAQ" href={`${publicFacingDomain}/faq?canGoBack=true`} />
                </li>
                <li>
                  <ExternalLink title="Contact" href={`${publicFacingDomain}#contact`} />
                </li>
              </ul>
            </div>
          </SidebarFooter>
        )}
      </SidebarContent>
    </Sidebar>
  );
}
